import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Badge, Button, Form, InputGroup, ListGroup, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { ALLOW_IN_MVP, environment } from '../../config/environment'
import { FileVersion, ShareLink, shareLinkService } from '../../services/share-link.service'
import { toastService } from '../../services/toast.service'
import { copyToClipboard } from '../../utils/clipboard'
import Phase2NoticeModal from './Phase2NoticeModal'

interface ShareLinksDialogProps {
  show: boolean
  onHide: () => void
  documentId: number
  hasArchiveVersion?: boolean
}

const ShareLinksDialog: React.FC<ShareLinksDialogProps> = ({ show, onHide, documentId, hasArchiveVersion = true }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([])
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState<number | null>(null)
  const [expirationDays, setExpirationDays] = useState<number | null>(7)
  const [useArchiveVersion, setUseArchiveVersion] = useState(hasArchiveVersion)
  const [showTotpPhase2Modal, setShowTotpPhase2Modal] = useState(false)

  const EXPIRATION_OPTIONS = [
    { label: t('one_day'), value: 1 },
    { label: t('seven_days'), value: 7 },
    { label: t('thirty_days'), value: 30 },
    { label: t('never'), value: null as number | null },
  ]

  useEffect(() => {
    setUseArchiveVersion(hasArchiveVersion)
  }, [hasArchiveVersion])

  useEffect(() => {
    if (show && documentId !== undefined) {
      refresh()
    }
  }, [show, documentId])

  const refresh = async () => {
    if (documentId === undefined) return
    setLoading(true)
    try {
      const results = await shareLinkService.getLinksForDocument(documentId)
      setShareLinks(results)
      setLoading(false)
    } catch (e) {
      toastService.showError(t('error_retrieving_links'), e)
      setLoading(false)
    }
  }

  const getShareUrl = (link: ShareLink): string => {
    const apiURL = typeof window !== 'undefined' ? new URL(environment.apiBaseUrl, window.location.origin) : new URL(environment.apiBaseUrl)
    return `${apiURL.origin}${apiURL.pathname.replace(/\/api\/$/, '/share/')}${link.slug}`
  }

  const getDaysRemaining = (link: ShareLink): string => {
    if (!link.expiration) return t('never_expires')
    const days = Math.round((Date.parse(link.expiration) - Date.now()) / (1000 * 60 * 60 * 24))
    return days === 1 ? t('one_day') : t('days', { count: days })
  }

  const copy = async (link: ShareLink) => {
    try {
      await copyToClipboard(getShareUrl(link))
      setCopied(link.id)
      setTimeout(() => {
        setCopied(null)
      }, 3000)
    } catch (err) {
      toastService.showError(t('failed_to_copy_to_clipboard'), err)
    }
  }

  const canShare = (link: ShareLink): boolean => {
    return navigator?.canShare && navigator.canShare({ url: getShareUrl(link) })
  }

  const share = (link: ShareLink) => {
    navigator.share({ url: getShareUrl(link) })
  }

  const deleteLink = async (link: ShareLink) => {
    try {
      await shareLinkService.deleteLink(link)
      void queryClient.invalidateQueries({ queryKey: ['document-history', documentId] })
      toastService.showSuccess(t('link_deleted_successfully'))
      refresh()
    } catch (e) {
      toastService.showError(t('error_deleting_link'), e)
    }
  }

  const createLink = async () => {
    if (ALLOW_IN_MVP) {
      setShowTotpPhase2Modal(true)
    } else {
      let expiration: Date | null = null
      if (expirationDays) {
        expiration = new Date()
        expiration.setDate(expiration.getDate() + expirationDays)
      }
      setLoading(true)
      try {
        const result = await shareLinkService.createLinkForDocument(
          documentId,
          useArchiveVersion ? FileVersion.Archive : FileVersion.Original,
          expiration
        )
        setLoading(false)
        setTimeout(() => {
          copy(result)
        }, 10)
        void queryClient.invalidateQueries({ queryKey: ['document-history', documentId] })
        refresh()
        toastService.showSuccess(t('link_created_successfully'))
      } catch (e) {
        setLoading(false)
        toastService.showError(t('error_creating_link'), e)
      }
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t('share_links')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <ListGroup variant="flush">
          {!shareLinks || shareLinks.length === 0 ? (
            <ListGroup.Item className="fst-italic small text-center text-secondary">{t('no_existing_links')}</ListGroup.Item>
          ) : (
            shareLinks.map((link) => (
              <ListGroup.Item key={link.id}>
                <InputGroup className="w-100">
                  <Form.Control type="text" value={getShareUrl(link)} readOnly />
                  {link.expiration && <InputGroup.Text>{getDaysRemaining(link)}</InputGroup.Text>}
                  <Button variant="outline-primary" onClick={() => copy(link)}>
                    {copied !== link.id ? <i className="bi bi-clipboard-fill"></i> : <i className="bi bi-clipboard-check-fill"></i>}
                    <span className="visually-hidden">{t('copy')}</span>
                  </Button>
                  {canShare(link) && (
                    <Button variant="outline-primary" onClick={() => share(link)}>
                      <i className="bi bi-box-arrow-up"></i>
                      <span className="visually-hidden">{t('share')}</span>
                    </Button>
                  )}
                  <Button variant="outline-danger" onClick={() => deleteLink(link)}>
                    <i className="bi bi-trash"></i>
                    <span className="visually-hidden">{t('delete')}</span>
                  </Button>
                </InputGroup>
                {copied === link.id && (
                  <Badge
                    bg="primary"
                    className="copied-badge small fade show ms-4 position-absolute top-50 translate-middle-y pe-none"
                    style={{ zIndex: 3 }}
                  >
                    {t('copied')}
                  </Badge>
                )}
              </ListGroup.Item>
            ))
          )}
        </ListGroup>
      </Modal.Body>
      <Modal.Footer>
        <div className="w-100">
          <div className="form-check form-switch ms-auto mb-2">
            <Form.Check
              type="switch"
              id="versionSwitch"
              label={t('share_archive_version')}
              disabled={!hasArchiveVersion}
              checked={useArchiveVersion}
              onChange={(e) => setUseArchiveVersion(e.target.checked)}
            />
          </div>
          <InputGroup>
            <InputGroup.Text>{t('expires')}:</InputGroup.Text>
            <Form.Select value={expirationDays ?? ''} onChange={(e) => setExpirationDays(e.target.value === '' ? null : parseInt(e.target.value))}>
              {EXPIRATION_OPTIONS.map((option) => (
                <option key={option.label} value={option.value ?? ''}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Button variant="outline-primary" onClick={createLink} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-plus"></i>}
              {t('create')}
            </Button>
          </InputGroup>
        </div>
      </Modal.Footer>
      <Phase2NoticeModal show={showTotpPhase2Modal} onClose={() => setShowTotpPhase2Modal(false)} />
    </Modal>
  )
}

export default ShareLinksDialog
